import {Palette} from 'hubbl-shared';
import {memo, useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {useAppSelector} from '../../hooks';
import {useStorageImageUrl} from '../../hooks/api/useStorageImageUrl';
import {UserSelector} from '../../store/user/UserSelector';
import {PaddingRight} from '../padding/PaddingRight';
import {TextHeading7} from '../text/TextHeading7';
import {TextHeading8} from '../text/TextHeading8';
import {UserInfoDropdown} from '../dropdown/UserInfoDropdown';
import {InitialsAvatar} from '../avatar/InitialsAvatar';

interface UserInfoDisplayProps {
  isCompact?: boolean;
}

export const UserInfoDisplay = memo(
  ({isCompact = false}: UserInfoDisplayProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [imageError, setImageError] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const profilePic = useStorageImageUrl(userDetails?.photoUrl);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <Container ref={containerRef}>
        <ClickableArea onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          {!imageError && profilePic ? (
            <StyledProfilePicture
              src={profilePic}
              $isCompact={isCompact}
              onError={() => setImageError(true)}
            />
          ) : (
            <InitialsAvatar
              name={userDetails?.fullName || ''}
              size={isCompact ? 24 : 32}
              fontSize={isCompact ? 10 : 12}
            />
          )}
          <PaddingRight amount={8} />
          <div>
            <TextHeading7 text={userDetails?.fullName} />
            {!isCompact && (
              <TextHeading8
                color={Palette.almostBlack750}
                text={userDetails?.companyName}
              />
            )}
          </div>
        </ClickableArea>
        <UserInfoDropdown
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
        />
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
`;

const ClickableArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

const StyledProfilePicture = styled.img<{$isCompact: boolean}>`
  width: ${props => (props.$isCompact ? '24px' : '32px')};
  height: ${props => (props.$isCompact ? '24px' : '32px')};
  border-radius: ${props => (props.$isCompact ? '12px' : '16px')};
  object-fit: cover;
  object-position: center;
`;
